import React, { useEffect, useState } from 'react'
import Logo from '../../images/logo-dark.svg'
import CheckoutForm, { BillingDetails } from '../../partials/booking/StripeCheckoutForm'
import { Link, useHistory } from 'react-router-dom'
import BookingLogin from '../../partials/booking/BookingLogin'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import Review from '../../components/Review'
import { logError, removeTransfer } from '../../store/slices/app'
import { formatCurrency, PageURLs } from '../../common'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import useOrders from '../../hooks/useOrders'
import { Transfer } from '../../types'
import ListGroup from '../../components/ListGroup'
import TransferSummaryListItem from '../../components/TransferSummaryList/TransferSummaryListItem'
import Page from '../../components/Page'
import Alert from '../../components/Alert'
import Icons from '../../components/Icons'
import Notice from '../../components/Notice'
import Drawer from '../../components/Drawer'
import { Disclaimer } from '../../hooks/useDisclaimers'
import NavigationArrow from '../../components/NavigationArrow'

const Checkout = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const orders = useOrders()
  const { user, transfers, disclaimers } = useSelector((state: RootState) => state.app)
  const [isLoading, setIsLoading] = useState(false)
  const total = Object.values(transfers).reduce((prev, curr) => prev + curr.total, 0)
  const [paymentError, setPaymentError] = useState(false)
  const [error, setError] = useState('')
  const [currentDisclaimers, setCurrentDisclaimers] = useState<Disclaimer[]>([])
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)

  useEffect(() => {
    if (Object.keys(transfers).length === 0) {
      history.push(PageURLs.BookNow)
    }
  }, [])

  ///
  /// UI Event Handlers
  ///
  const onRemoveTransfer = (transfer: Transfer) => {
    dispatch(removeTransfer(transfer.booking_id))

    if (Object.keys(transfers).length <= 1) {
      history.push(PageURLs.BookNow)
    }
  }

  const onShowDisclaimers = (disclaimers: Disclaimer[]) => {
    setCurrentDisclaimers(disclaimers.map((x) => ({ ...x })))
    setIsDrawerVisible(true)
  }

  const onHideDisclaimers = () => {
    setIsDrawerVisible(false)
    setCurrentDisclaimers([])
  }

  const stripe = useStripe()
  const elements = useElements()

  const handleSubmit = async (billingDetails: BillingDetails) => {
    if (!stripe || !elements || !user) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    if (isLoading) {
      return
    }

    const result = await elements.submit()

    if (result.error) {
      return
    }

    setIsLoading(true)
    setPaymentError(false)
    setError('')

    try {
      const { data: order } = await orders.store(user, Object.values(transfers))

      const paymentIntentResult = await stripe.confirmPayment({
        elements,
        clientSecret: order.payment_client_secret,
        confirmParams: {
          return_url: window.location.origin + PageURLs.OrderConfirmation.replace(':order', order.order_id.toString()),
          payment_method_data: {
            billing_details: {
              name: billingDetails.name,
              email: billingDetails.email,
            },
          },
        },
      })

      if (paymentIntentResult.error) {
        setIsLoading(false)
        setPaymentError(true)
        return
      }
    } catch (error) {
      dispatch(logError(error))
      setIsLoading(false)
      setError('Failed to create order.')
      return
    }
  }

  return (
    <>
      <Page className="bg-white">
        <div className="container">
          <div className="row justify-content-center pt-4 pt-lg-5">
            <div className="centered-logo-header">
              <Link to="/">
                <img src={Logo} alt="Logo" />
              </Link>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <Link to={PageURLs.BookNow} className="text-secondary">
                <span className="icon-arrow-right direction-left d-inline-block"></span>
              </Link>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-lg-10">
              <div className="row mt-5 mt-lg-6">
                <div className="col">
                  <h3>Complete Booking</h3>
                </div>
              </div>

              <div className="row justify-content-center mt-3">
                <div className="col-12 col-lg-6">
                  <ListGroup.Items>
                    {Object.values(transfers).map((transfer) => (
                      <ListGroup.Item key={transfer.booking_id}>
                        <TransferSummaryListItem
                          key={transfer.booking_id}
                          transfer={transfer}
                          onRemove={onRemoveTransfer}
                        >
                          {disclaimers[transfer.booking_id] && (
                            <div className="mt-2">
                              <Notice
                                variant="info"
                                onClick={(e) => {
                                  e.preventDefault()
                                  onShowDisclaimers(disclaimers[transfer.booking_id])
                                }}
                              >
                                <span className="d-flex align-items-center">
                                  <Icons.Exclamation className="flex-shrink-0" />
                                  <span className="ml-2">
                                    Please review these important notes and disclaimers which may affect your booking.
                                  </span>
                                </span>
                              </Notice>
                            </div>
                          )}
                        </TransferSummaryListItem>
                      </ListGroup.Item>
                    ))}

                    <ListGroup.Item>
                      <Link to={PageURLs.AddTransfer} className="btn btn-link p-4 d-block text-secondary">
                        <span className="bg-light-blue p-2">
                          <span className="icon-plus"></span>
                        </span>
                        <span className="ml-3">Add Another Transfer</span>
                      </Link>
                    </ListGroup.Item>
                  </ListGroup.Items>

                  <div className="d-flex p-3 justify-content-between">
                    <span className="h5 ml-auto">Total: £{formatCurrency(total)}</span>
                  </div>

                  <Review
                    author="Simon Willkinson"
                    stars={5}
                    date="2 weeks ago"
                    content="This was the first time I used AirportCars over Uber to go to the airport. The driver was great,
                      and patient (forgot my passport and had to go back and collect it). Price wise nearly half of the
                      quoted Uber for the same journey. I will definitely use the..."
                  />
                </div>
                <div className="col-12 col-lg-6 pl-lg-5">
                  {user ? (
                    <>
                      {/*<div className="payment-methods-wrap bg-pink p-3 d-flex mt-3 mt-md-0">*/}
                      {/*  <span className="pr-3">*/}
                      {/*    <span className="icon-credit-card-alt"></span>*/}
                      {/*  </span>*/}
                      {/*  <span className="flex-grow-1">Card Payment</span>*/}
                      {/*  <a href="/book-now/complete-booking/" className="placeholder-text small-text text-secondary">*/}
                      {/*    <u>switch</u>*/}
                      {/*  </a>*/}
                      {/*</div>*/}

                      <div className="payment-form-wrap mt-4">
                        <>
                          {!!error && <Alert type="danger" msg={error} />}
                          <CheckoutForm isLoading={isLoading} hasPaymentError={paymentError} onSubmit={handleSubmit} />
                        </>
                      </div>
                    </>
                  ) : (
                    <>
                      <BookingLogin />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Page>
      <Drawer visible={isDrawerVisible} onBackgroundClick={onHideDisclaimers}>
        <NavigationArrow onClick={onHideDisclaimers} compact />
        <div className="pb-4">
          <h4>Important notes and disclaimers</h4>

          {currentDisclaimers.map((disclaimer, idx) => (
            <ListGroup.Items key={`${disclaimer.title}-${idx}`} className="mb-4">
              <ListGroup.Title>{disclaimer.title}</ListGroup.Title>
              <ListGroup.Item className="p-4">{disclaimer.description}</ListGroup.Item>
            </ListGroup.Items>
          ))}
        </div>
      </Drawer>
    </>
  )
}

export default Checkout
